import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import { useRequiredContext } from "@redotech/react-util/context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSupportTicketsTableContext } from "../table/support-tickets-table-context";

/**
 * Auto-advances to the next conversation when the current one
 * leaves the filtered list (e.g., after a status change).
 */
export function useAutoAdvanceSupportTicket(
  supportTicketId: SupportTicketId,
): void {
  const { data, openSupportTicket } = useSupportTicketsTableContext();
  const navigate = useNavigate();
  const location = useLocation();
  const team = useRequiredContext(TeamContext);

  const wasInListRef = useRef(false);
  const nextSupportTicketIdRef = useRef<SupportTicketId | null>(null);

  useEffect(() => {
    const currentIndex = data.findIndex((c) => c.id === supportTicketId);
    if (currentIndex !== -1) {
      wasInListRef.current = true;
      if (currentIndex < data.length - 1) {
        nextSupportTicketIdRef.current = data[currentIndex + 1]!.id;
      } else if (currentIndex > 0) {
        nextSupportTicketIdRef.current = data[currentIndex - 1]!.id;
      } else {
        nextSupportTicketIdRef.current = null;
      }
      return;
    }

    if (data.length === 0) {
      wasInListRef.current = false;
      return;
    }
    if (wasInListRef.current) {
      wasInListRef.current = false;
      if (nextSupportTicketIdRef.current) {
        openSupportTicket(nextSupportTicketIdRef.current);
      } else {
        navigate(`/stores/${team._id}/support-v2${location.search}`);
      }
    }
  }, [
    data,
    supportTicketId,
    openSupportTicket,
    navigate,
    team._id,
    location.search,
  ]);
}
